import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, FileText, User, DollarSign, Calendar, Loader2, CheckCircle, AlertCircle, Settings, Users, Image, Handshake } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { userService } from '@/services/userService';
import { useUser } from '@/contexts/UserContext';
import * as pdfjsLib from 'pdfjs-dist';

interface PDFDealUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDealCreated: () => void;
  createDeal: (data: any) => Promise<any>;
}

interface ExtractedDealData {
  title: string;
  description: string;
  deal_type: string;
  deal_value: number;
  estimated_completion_date: string;
  priority: 'low' | 'medium' | 'high';
  status: string;
  // Contract terms
  start_date?: string;
  end_date?: string;
  duration_months?: number;
  // Exclusivity
  exclusive_category?: string;
  region?: string;
  // Payment structure
  payment_frequency?: string;
  equity_component?: number;
  quarterly_payments?: boolean;
  // Deliverables
  deliverables?: Array<{
    type: string;
    platform?: string;
    quantity: number;
    frequency: string;
    description?: string;
  }>;
  // Rights and usage
  image_rights?: boolean;
  tagging_requirements?: string;
  content_usage_rights?: string;
  // Advisory/collaboration
  advisory_board?: boolean;
  advisory_meetings?: string;
  // Press and PR
  pr_sessions?: number;
  press_requirements?: string;
  // Physical requirements
  brand_placement?: string;
  gifting_requirements?: string;
}

export const PDFDealUploadModal: React.FC<PDFDealUploadModalProps> = ({
  isOpen,
  onClose,
  onDealCreated,
  createDeal
}) => {
  const { accountType } = useUser();
  const userType = accountType === 'venture' ? 'venture' : 'brand';
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [extractedData, setExtractedData] = useState<ExtractedDealData | null>(null);
  const [athletes, setAthletes] = useState<any[]>([]);
  const [selectedAthlete, setSelectedAthlete] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [extractedText, setExtractedText] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const { toast } = useToast();

  // Fetch athletes when modal opens
  React.useEffect(() => {
    const fetchAthletes = async () => {
      try {
        const athleteProfiles = await userService.getTalents();
        setAthletes(athleteProfiles);
      } catch (error) {
        console.error('Failed to fetch athletes:', error);
      }
    };
    
    if (isOpen) {
      fetchAthletes();
    }
  }, [isOpen]);

  const steps = [
    { id: 'upload', title: 'Upload PDF', icon: Upload },
    { id: 'review', title: 'Review Data', icon: FileText },
    { id: 'athlete', title: 'Select Athlete', icon: User },
    { id: 'finalize', title: 'Create Deal', icon: CheckCircle },
  ];

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      // Set up PDF.js worker (v4+ uses .mjs, track installed version)
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      const maxPages = Math.min(pdf.numPages, 10); // Limit to first 10 pages
      
      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        setProgress((pageNum / maxPages) * 50); // 50% for text extraction
        
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        const pageText = textContent.items
          .filter((item: any) => item.str && item.str.trim().length > 0)
          .map((item: any) => item.str)
          .join(' ');
        
        if (pageText.trim()) {
          fullText += pageText + '\n\n';
        }
      }
      
      // Clean up the text
      fullText = fullText
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s.,!?;:'"()\-\$%@#]/g, ' ')
        .trim();
      
      if (fullText.length < 50) {
        throw new Error('Could not extract meaningful text from PDF');
      }
      
      return fullText;
    } catch (error) {
      console.error('PDF extraction error:', error);
      throw new Error(`Failed to extract text from PDF: ${error.message}`);
    }
  };

  const extractDealInformation = (text: string): ExtractedDealData => {
    const lowerText = text.toLowerCase();
    
    // Extract title from partnership names
    let title = 'Imported Deal';
    const titlePatterns = [
      /between:\s*(?:1\.\s*)?([^\n]+?)\s*(?:2\.\s*)?([^\n]+)/i,
      /heads of terms between[:\s]*([^\n]+)/i,
      /partnership.{0,30}agreement[:\s]*([^\n]{1,100})/i,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s*x\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i
    ];
    
    for (const pattern of titlePatterns) {
      const match = text.match(pattern);
      if (match) {
        if (match[2]) {
          title = `${match[1].trim()} x ${match[2].trim()}`;
        } else if (match[1]) {
          title = match[1].trim();
        }
        break;
      }
    }
    
    // Extract deal value and equity
    let dealValue = 0;
    let equityComponent = 0;
    const valueMatches = text.match(/\$[\d,]+(?:\.?\d{0,2})?(?:\s*(?:k|usd|per year))?/gi);
    if (valueMatches && valueMatches.length > 0) {
      const amounts = valueMatches.map(v => {
        let amount = parseFloat(v.replace(/[$,kusd]/gi, ''));
        if (v.toLowerCase().includes('k')) amount *= 1000;
        return amount;
      });
      dealValue = amounts.reduce((sum, val) => sum + val, 0);
    }
    
    // Extract equity separately
    const equityMatch = text.match(/\$(\d+(?:,\d{3})*(?:k)?)\s*equity/i);
    if (equityMatch) {
      equityComponent = parseFloat(equityMatch[1].replace(/[,$k]/gi, ''));
      if (equityMatch[1].toLowerCase().includes('k')) equityComponent *= 1000;
    }
    
    // Extract contract dates and duration
    let startDate = '';
    let endDate = '';
    let durationMonths = 0;
    
    const termMatch = text.match(/(?:term|duration):\s*([^–\n]+)(?:–|-)?\s*([^\n]*)/i);
    if (termMatch) {
      const datePattern = /(\w+\s+\d{1,2},?\s+\d{4})|(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/gi;
      const dates = termMatch[0].match(datePattern);
      if (dates && dates.length >= 2) {
        startDate = new Date(dates[0]).toISOString().split('T')[0];
        endDate = new Date(dates[1]).toISOString().split('T')[0];
      }
    }
    
    const monthsMatch = text.match(/(\d+)\s*months/i);
    if (monthsMatch) {
      durationMonths = parseInt(monthsMatch[1]);
    }
    
    // Extract exclusivity
    let exclusiveCategory = '';
    let region = '';
    const exclusivityMatch = text.match(/exclusive category:\s*([^\n]+)/i);
    if (exclusivityMatch) {
      exclusiveCategory = exclusivityMatch[1].trim();
    }
    
    const regionMatch = text.match(/region:\s*([^\n]+)/i);
    if (regionMatch) {
      region = regionMatch[1].trim();
    }
    
    // Extract payment frequency
    let paymentFrequency = '';
    let quarterlyPayments = false;
    if (text.toLowerCase().includes('quarterly')) {
      paymentFrequency = 'quarterly';
      quarterlyPayments = true;
    } else if (text.toLowerCase().includes('monthly')) {
      paymentFrequency = 'monthly';
    } else if (text.toLowerCase().includes('annually')) {
      paymentFrequency = 'annually';
    }
    
    // Extract deliverables
    const deliverables: Array<{type: string; platform?: string; quantity: number; frequency: string; description?: string}> = [];
    
    // Social media deliverables
    const socialPatterns = [
      { platform: 'TikTok', pattern: /(\d+)\s*tiktok\s*posts?\s*per\s*(year|month)/i },
      { platform: 'Instagram', pattern: /(\d+)\s*(?:instagram\s*)?(?:reels?|posts?|stories)\s*(?:per\s*)?(year|month)/i },
      { platform: 'YouTube', pattern: /(\d+)\s*youtube\s*videos?\s*per\s*(year|month)/i }
    ];
    
    socialPatterns.forEach(({ platform, pattern }) => {
      const match = text.match(pattern);
      if (match) {
        deliverables.push({
          type: 'social_media',
          platform,
          quantity: parseInt(match[1]),
          frequency: match[2],
          description: match[0]
        });
      }
    });
    
    // Extract rights and usage
    let imageRights = false;
    let taggingRequirements = '';
    let contentUsageRights = '';
    
    if (text.toLowerCase().includes('image rights') || text.toLowerCase().includes('intellectual property')) {
      imageRights = true;
    }
    
    const taggingMatch = text.match(/tag[ging\s]*([^\n\.]+)/i);
    if (taggingMatch) {
      taggingRequirements = taggingMatch[1].trim();
    }
    
    if (text.toLowerCase().includes('content') && text.toLowerCase().includes('use')) {
      contentUsageRights = 'Personal and promotional use rights included';
    }
    
    // Extract advisory details
    let advisoryBoard = false;
    let advisoryMeetings = '';
    
    if (text.toLowerCase().includes('advisory board')) {
      advisoryBoard = true;
      const meetingsMatch = text.match(/(?:quarterly|monthly|annual).*(?:meeting|collaboration)/i);
      if (meetingsMatch) {
        advisoryMeetings = meetingsMatch[0];
      }
    }
    
    // Extract PR requirements
    let prSessions = 0;
    let pressRequirements = '';
    
    const prMatch = text.match(/(\d+).*(?:media|pr|interview).*session/i);
    if (prMatch) {
      prSessions = parseInt(prMatch[1]);
      pressRequirements = prMatch[0];
    }
    
    // Extract brand placement
    let brandPlacement = '';
    const placementMatch = text.match(/(?:brand|logo).*(?:sticker|placement).*([^\n]+)/i);
    if (placementMatch) {
      brandPlacement = placementMatch[1].trim();
    }
    
    // Extract gifting requirements
    let giftingRequirements = '';
    if (text.toLowerCase().includes('gifting') || text.toLowerCase().includes('seeding')) {
      giftingRequirements = 'Gifting and seeding opportunities included';
    }
    
    // Determine deal type
    let dealType = userType === 'venture' ? 'equity investment' : 'sponsorship';
    if (userType === 'brand') {
      if (lowerText.includes('advisor') || lowerText.includes('advisory')) {
        dealType = 'advisor';
      } else if (lowerText.includes('collaboration')) {
        dealType = 'collaboration';
      } else if (lowerText.includes('ambassador')) {
        dealType = 'ambassador';
      } else if (lowerText.includes('partner')) {
        dealType = 'partnership';
      }
    }
    
    // Determine priority
    let priority: 'low' | 'medium' | 'high' = 'medium';
    const totalValue = dealValue + equityComponent;
    if (totalValue > 100000) {
      priority = 'high';
    } else if (totalValue < 25000) {
      priority = 'low';
    }
    
    return {
      title: title || `${userType === 'venture' ? 'Investment' : 'Partnership'} Deal`,
      description: `Deal imported from PDF document. Original content: ${text.slice(0, 300)}...`,
      deal_type: dealType,
      deal_value: dealValue,
      estimated_completion_date: endDate || startDate,
      priority,
      status: 'pending',
      start_date: startDate,
      end_date: endDate,
      duration_months: durationMonths,
      exclusive_category: exclusiveCategory,
      region: region,
      payment_frequency: paymentFrequency,
      equity_component: equityComponent,
      quarterly_payments: quarterlyPayments,
      deliverables: deliverables.length > 0 ? deliverables : undefined,
      image_rights: imageRights,
      tagging_requirements: taggingRequirements,
      content_usage_rights: contentUsageRights,
      advisory_board: advisoryBoard,
      advisory_meetings: advisoryMeetings,
      pr_sessions: prSessions,
      press_requirements: pressRequirements,
      brand_placement: brandPlacement,
      gifting_requirements: giftingRequirements
    };
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;
    
    if (uploadedFile.type !== 'application/pdf') {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF file.",
        variant: "destructive",
      });
      return;
    }
    
    setFile(uploadedFile);
    setIsProcessing(true);
    setProgress(0);
    
    try {
      // Extract text from PDF
      const text = await extractTextFromPDF(uploadedFile);
      setExtractedText(text);
      setProgress(75);
      
      // Extract deal information
      const dealData = extractDealInformation(text);
      setExtractedData(dealData);
      setProgress(100);
      
      toast({
        title: "PDF processed successfully",
        description: "Deal information has been extracted. Please review and continue.",
      });
      
      setCurrentStep(1); // Move to review step
    } catch (error) {
      console.error('Processing error:', error);
      toast({
        title: "Processing failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDataUpdate = (field: string, value: any) => {
    if (extractedData) {
      setExtractedData({ ...extractedData, [field]: value });
    }
  };

  const handleCreateDeal = async () => {
    if (!extractedData) return;
    
    setIsProcessing(true);
    try {
      const dealData = {
        ...extractedData,
        talent_profile_id: selectedAthlete === "none" ? null : selectedAthlete || null,
        deal_category: userType === 'venture' ? 'investment' : 'sponsorship',
        // Add imported flag to track this was imported from PDF
        description: `${extractedData.description}\n\n[Imported from PDF: ${file?.name}]`
      };
      
      await createDeal(dealData);
      
      toast({
        title: "Deal created successfully",
        description: "Your imported deal has been created and is now available in your dashboard.",
      });
      
      onDealCreated();
      resetModal();
      onClose();
    } catch (error) {
      console.error('Deal creation error:', error);
      toast({
        title: "Failed to create deal",
        description: "Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setExtractedData(null);
    setSelectedAthlete('');
    setCurrentStep(0);
    setProgress(0);
    setExtractedText('');
    setIsProcessing(false);
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 0: return file && extractedData;
      case 1: return extractedData?.title && extractedData?.deal_type;
      case 2: return true; // Athlete selection is optional
      default: return false;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Existing Deal</DialogTitle>
        </DialogHeader>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                index <= currentStep ? 'border-primary bg-primary text-primary-foreground' : 'border-muted'
              }`}>
                <step.icon className="w-5 h-5" />
              </div>
              <span className={`ml-2 text-sm ${index <= currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-4 ${index < currentStep ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        {currentStep === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload PDF Document
              </CardTitle>
              <CardDescription>
                Upload a PDF of your existing deal or agreement to extract information automatically
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="pdf-upload"
                />
                <Label
                  htmlFor="pdf-upload"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer mb-2"
                >
                  Choose PDF File
                </Label>
                <p className="text-sm text-muted-foreground">
                  Supported format: PDF (max 10 pages will be processed)
                </p>
              </div>
              
              {isProcessing && (
                <div className="space-y-2">
                  <Progress value={progress} className="w-full" />
                  <p className="text-sm text-center text-muted-foreground">
                    Processing PDF... {Math.round(progress)}%
                  </p>
                </div>
              )}
              
              {file && !isProcessing && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <FileText className="w-5 h-5" />
                  <span className="text-sm">{file.name}</span>
                  <CheckCircle className="w-5 h-5 text-success ml-auto" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 1 && extractedData && (
          <Card>
            <CardHeader>
              <CardTitle>Review Extracted Information</CardTitle>
              <CardDescription>
                Please review and edit the comprehensive partnership details extracted from your PDF
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Basic Info
                  </TabsTrigger>
                  <TabsTrigger value="terms" className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Contract Terms
                  </TabsTrigger>
                  <TabsTrigger value="deliverables" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Deliverables
                  </TabsTrigger>
                  <TabsTrigger value="rights" className="flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Rights & Usage
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Deal Title</Label>
                      <Input
                        id="title"
                        value={extractedData.title}
                        onChange={(e) => handleDataUpdate('title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="deal_type">Deal Type</Label>
                      <Select
                        value={extractedData.deal_type}
                        onValueChange={(value) => handleDataUpdate('deal_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {userType === 'venture' ? (
                            <>
                              <SelectItem value="equity investment">Equity Investment</SelectItem>
                              <SelectItem value="convertible note">Convertible Note</SelectItem>
                              <SelectItem value="safe">SAFE</SelectItem>
                              <SelectItem value="follow-on">Follow-on</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="sponsorship">Sponsorship</SelectItem>
                              <SelectItem value="collaboration">Collaboration</SelectItem>
                              <SelectItem value="ambassador">Brand Ambassador</SelectItem>
                              <SelectItem value="advisor">Advisor</SelectItem>
                              <SelectItem value="partnership">Partnership</SelectItem>
                              <SelectItem value="event">Event Partnership</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="deal_value">Cash Value ($)</Label>
                      <Input
                        id="deal_value"
                        type="number"
                        value={extractedData.deal_value}
                        onChange={(e) => handleDataUpdate('deal_value', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="equity_component">Equity Value ($)</Label>
                      <Input
                        id="equity_component"
                        type="number"
                        value={extractedData.equity_component || 0}
                        onChange={(e) => handleDataUpdate('equity_component', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={extractedData.priority}
                        onValueChange={(value) => handleDataUpdate('priority', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={extractedData.status}
                        onValueChange={(value) => handleDataUpdate('status', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={extractedData.description}
                      onChange={(e) => handleDataUpdate('description', e.target.value)}
                      rows={3}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="terms" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Start Date</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={extractedData.start_date || ''}
                        onChange={(e) => handleDataUpdate('start_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={extractedData.end_date || ''}
                        onChange={(e) => handleDataUpdate('end_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="duration_months">Duration (Months)</Label>
                      <Input
                        id="duration_months"
                        type="number"
                        value={extractedData.duration_months || ''}
                        onChange={(e) => handleDataUpdate('duration_months', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="payment_frequency">Payment Frequency</Label>
                      <Select
                        value={extractedData.payment_frequency || ''}
                        onValueChange={(value) => handleDataUpdate('payment_frequency', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annually">Annually</SelectItem>
                          <SelectItem value="milestone">Per Milestone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="exclusive_category">Exclusive Category</Label>
                      <Input
                        id="exclusive_category"
                        value={extractedData.exclusive_category || ''}
                        onChange={(e) => handleDataUpdate('exclusive_category', e.target.value)}
                        placeholder="e.g., Camera, Sportswear"
                      />
                    </div>
                    <div>
                      <Label htmlFor="region">Region</Label>
                      <Input
                        id="region"
                        value={extractedData.region || ''}
                        onChange={(e) => handleDataUpdate('region', e.target.value)}
                        placeholder="e.g., Global, North America"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="quarterly_payments"
                      checked={extractedData.quarterly_payments || false}
                      onCheckedChange={(checked) => handleDataUpdate('quarterly_payments', checked)}
                    />
                    <Label htmlFor="quarterly_payments">Quarterly payments structure</Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="advisory_board"
                      checked={extractedData.advisory_board || false}
                      onCheckedChange={(checked) => handleDataUpdate('advisory_board', checked)}
                    />
                    <Label htmlFor="advisory_board">Advisory board participation</Label>
                  </div>
                  
                  {extractedData.advisory_board && (
                    <div>
                      <Label htmlFor="advisory_meetings">Advisory Meeting Details</Label>
                      <Input
                        id="advisory_meetings"
                        value={extractedData.advisory_meetings || ''}
                        onChange={(e) => handleDataUpdate('advisory_meetings', e.target.value)}
                        placeholder="e.g., Quarterly meetings, R&D collaboration"
                      />
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="deliverables" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    {extractedData.deliverables && extractedData.deliverables.length > 0 ? (
                      <div className="space-y-3">
                        <Label>Extracted Social Media Deliverables</Label>
                        {extractedData.deliverables.map((deliverable, index) => (
                          <div key={index} className="p-3 border rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="outline">{deliverable.platform}</Badge>
                              <span className="text-sm text-muted-foreground">
                                {deliverable.quantity} per {deliverable.frequency}
                              </span>
                            </div>
                            <p className="text-sm">{deliverable.description}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground py-8">
                        <Users className="w-12 h-12 mx-auto mb-2" />
                        <p>No specific deliverables were extracted from the PDF</p>
                        <p className="text-sm">You can add deliverables manually after creating the deal</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="pr_sessions">PR/Media Sessions</Label>
                        <Input
                          id="pr_sessions"
                          type="number"
                          value={extractedData.pr_sessions || 0}
                          onChange={(e) => handleDataUpdate('pr_sessions', parseInt(e.target.value) || 0)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="brand_placement">Brand Placement Requirements</Label>
                        <Input
                          id="brand_placement"
                          value={extractedData.brand_placement || ''}
                          onChange={(e) => handleDataUpdate('brand_placement', e.target.value)}
                          placeholder="e.g., Logo on surfboard"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="press_requirements">Press & PR Requirements</Label>
                      <Textarea
                        id="press_requirements"
                        value={extractedData.press_requirements || ''}
                        onChange={(e) => handleDataUpdate('press_requirements', e.target.value)}
                        rows={2}
                        placeholder="Details about media sessions, interviews, etc."
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="gifting_requirements">Gifting & Seeding</Label>
                      <Input
                        id="gifting_requirements"
                        value={extractedData.gifting_requirements || ''}
                        onChange={(e) => handleDataUpdate('gifting_requirements', e.target.value)}
                        placeholder="Gifting opportunities and requirements"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="rights" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="image_rights"
                        checked={extractedData.image_rights || false}
                        onCheckedChange={(checked) => handleDataUpdate('image_rights', checked)}
                      />
                      <Label htmlFor="image_rights">Image and likeness rights included</Label>
                    </div>
                    
                    <div>
                      <Label htmlFor="tagging_requirements">Tagging Requirements</Label>
                      <Input
                        id="tagging_requirements"
                        value={extractedData.tagging_requirements || ''}
                        onChange={(e) => handleDataUpdate('tagging_requirements', e.target.value)}
                        placeholder="Social media tagging requirements"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="content_usage_rights">Content Usage Rights</Label>
                      <Textarea
                        id="content_usage_rights"
                        value={extractedData.content_usage_rights || ''}
                        onChange={(e) => handleDataUpdate('content_usage_rights', e.target.value)}
                        rows={3}
                        placeholder="Details about content usage rights and restrictions"
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              
              {extractedText && (
                <div className="mt-6">
                  <Label>Extracted Text Preview</Label>
                  <div className="p-3 bg-muted rounded-lg max-h-40 overflow-y-auto mt-2">
                    <pre className="text-xs whitespace-pre-wrap">{extractedText.slice(0, 500)}...</pre>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Link to Athlete (Optional)</CardTitle>
              <CardDescription>
                Select an athlete to link this deal to, or leave blank for marketplace posting
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedAthlete} onValueChange={setSelectedAthlete}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an athlete (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific athlete (marketplace)</SelectItem>
                  {athletes.map((athlete) => (
                    <SelectItem key={athlete.id} value={athlete.id}>
                      {athlete.name} - {athlete.sport}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedAthlete && (
                <div className="mt-4">
                  {(() => {
                    const athlete = athletes.find(a => a.id === selectedAthlete);
                    return athlete ? (
                      <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                        {athlete.photo && (
                          <img 
                            src={athlete.photo} 
                            alt={athlete.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium">{athlete.name}</p>
                          <p className="text-sm text-muted-foreground">{athlete.sport}</p>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => {
              if (currentStep > 0) {
                setCurrentStep(currentStep - 1);
              } else {
                resetModal();
                onClose();
              }
            }}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          
          <Button
            onClick={() => {
              if (currentStep < 2) {
                setCurrentStep(currentStep + 1);
              } else {
                handleCreateDeal();
              }
            }}
            disabled={!canProceedToNext() || isProcessing}
          >
            {isProcessing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {currentStep < 2 ? 'Next' : 'Create Deal'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};